import { createFileRoute, useParams, Link } from '@tanstack/react-router';
import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { getErrorMessage } from '@weebee/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ErrorState } from '@/components/state/error-state';
import { queryKeys } from '@/lib/query-keys';
import { formatDateTime, formatRelative } from '@/lib/format';
import {
  getTicket,
  listTicketComments,
  updateTicket,
  addComment,
  type Ticket,
} from '@/features/support/api';
import {
  SUPPORT_STATUS,
  SUPPORT_PRIORITY,
  statusTone,
  priorityTone,
} from '@/features/support/constants';

export const Route = createFileRoute('/_authenticated/support/$id')({
  component: TicketDetailPage,
});

function TicketDetailPage() {
  const { id } = useParams({ from: '/_authenticated/support/$id' });

  const detail = useQuery({
    queryKey: queryKeys.support.detail(id),
    queryFn: () => getTicket(id),
  });

  const ticket = detail.data;

  return (
    <div className="mx-auto max-w-5xl p-8">
      <Link
        to="/support"
        className="wb-small"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 16 }}
      >
        <ArrowLeft size={15} /> Back to tickets
      </Link>

      {detail.isLoading ? (
        <span className="wb-skeleton" style={{ width: 240, height: 28, display: 'block' }} />
      ) : detail.error ? (
        <ErrorState error={detail.error} onRetry={() => detail.refetch()} />
      ) : ticket ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
            <h1 className="wb-h1">#{ticket.ticket_number}</h1>
            <Badge tone={statusTone(ticket.status)}>{ticket.status}</Badge>
            <Badge tone={priorityTone(ticket.priority)}>{ticket.priority}</Badge>
          </div>
          <p className="wb-sub" style={{ marginBottom: 20 }}>
            {ticket.category} · opened {formatRelative(ticket.created_at)} by{' '}
            {ticket.user_display_name}
          </p>

          <div className="grid gap-4 lg:grid-cols-3">
            <div
              className="lg:col-span-2"
              style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
            >
              <Card>
                <CardContent>
                  <div className="wb-eyebrow" style={{ marginBottom: 10 }}>
                    Message
                  </div>
                  <p style={{ whiteSpace: 'pre-wrap' }}>{ticket.message}</p>
                  {ticket.attachment_urls && ticket.attachment_urls.length > 0 ? (
                    <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {ticket.attachment_urls.map((url, i) => (
                        <a
                          key={url}
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          className="wb-small"
                        >
                          Attachment {i + 1}
                        </a>
                      ))}
                    </div>
                  ) : null}
                </CardContent>
              </Card>

              <CommentsThread ticketId={id} />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <EditPanel ticket={ticket} />
              <Card>
                <CardContent>
                  <div className="wb-eyebrow" style={{ marginBottom: 10 }}>
                    Details
                  </div>
                  <dl style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <Field label="User" value={ticket.user_display_name} />
                    <Field label="Platform" value={ticket.platform} />
                    <Field label="App version" value={ticket.app_version} />
                    <Field label="Build" value={ticket.app_build} />
                    <Field label="OS" value={ticket.os_version} />
                    <Field label="Device" value={ticket.device_model} />
                    <Field label="Locale" value={ticket.locale} />
                    <Field label="Screen" value={ticket.current_screen} />
                    <Field label="Assigned to" value={ticket.assigned_to} />
                    <Field label="Opened" value={formatDateTime(ticket.created_at)} />
                    <Field
                      label="Resolved"
                      value={ticket.resolved_at ? formatDateTime(ticket.resolved_at) : '—'}
                    />
                    {ticket.github_issue_number ? (
                      <Field label="GitHub" value={`#${ticket.github_issue_number}`} />
                    ) : null}
                  </dl>
                </CardContent>
              </Card>

              {ticket.diagnostic_info ? <DiagnosticPanel info={ticket.diagnostic_info} /> : null}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

function EditPanel({ ticket }: { ticket: Ticket }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState(ticket.status);
  const [priority, setPriority] = useState(ticket.priority);
  const [assignedTo, setAssignedTo] = useState(ticket.assigned_to ?? '');
  const [adminNotes, setAdminNotes] = useState(ticket.admin_notes ?? '');

  // Re-sync local form when the ticket refetches.
  useEffect(() => {
    setStatus(ticket.status);
    setPriority(ticket.priority);
    setAssignedTo(ticket.assigned_to ?? '');
    setAdminNotes(ticket.admin_notes ?? '');
  }, [ticket.id, ticket.status, ticket.priority, ticket.assigned_to, ticket.admin_notes]);

  const mutation = useMutation({
    mutationFn: () =>
      updateTicket(ticket.id, {
        status,
        priority,
        assigned_to: assignedTo.trim() || undefined,
        admin_notes: adminNotes.trim() || undefined,
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.support.detail(ticket.id) }),
        queryClient.invalidateQueries({ queryKey: ['support', 'list'] }),
        queryClient.invalidateQueries({ queryKey: queryKeys.support.stats() }),
      ]);
    },
  });

  const dirty =
    status !== ticket.status ||
    priority !== ticket.priority ||
    assignedTo !== (ticket.assigned_to ?? '') ||
    adminNotes !== (ticket.admin_notes ?? '');

  return (
    <Card>
      <CardContent>
        <div className="wb-eyebrow" style={{ marginBottom: 10 }}>
          Manage
        </div>
        <label className="wb-label">Status</label>
        <Select
          options={SUPPORT_STATUS}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        />
        <label className="wb-label" style={{ marginTop: 12 }}>
          Priority
        </label>
        <Select
          options={SUPPORT_PRIORITY}
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
        />
        <label className="wb-label" style={{ marginTop: 12 }}>
          Assigned to
        </label>
        <input
          className="wb-field"
          value={assignedTo}
          onChange={(e) => setAssignedTo(e.target.value)}
          placeholder="Admin id or email"
        />
        <label className="wb-label" style={{ marginTop: 12 }}>
          Admin notes
        </label>
        <textarea
          className="wb-field"
          rows={3}
          value={adminNotes}
          onChange={(e) => setAdminNotes(e.target.value)}
          placeholder="Internal notes (not shown to user)"
        />
        <Button
          size="sm"
          block
          style={{ marginTop: 14 }}
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending || !dirty}
        >
          {mutation.isPending ? 'Saving…' : 'Save changes'}
        </Button>
        {mutation.isError ? (
          <div className="wb-form-error">{getErrorMessage(mutation.error)}</div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function CommentsThread({ ticketId }: { ticketId: string }) {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState('');

  const comments = useQuery({
    queryKey: queryKeys.support.comments(ticketId),
    queryFn: () => listTicketComments(ticketId),
  });

  const mutation = useMutation({
    mutationFn: () => addComment(ticketId, { message: message.trim() }),
    onSuccess: async () => {
      setMessage('');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.support.comments(ticketId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.support.detail(ticketId) }),
      ]);
    },
  });

  const list = comments.data?.comments ?? [];

  return (
    <Card>
      <CardContent>
        <div className="wb-eyebrow" style={{ marginBottom: 12 }}>
          Comments
        </div>
        {comments.isLoading ? (
          <span className="wb-skeleton" style={{ width: '100%', height: 40, display: 'block' }} />
        ) : list.length === 0 ? (
          <p className="wb-small">No comments yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {list.map((c) => (
              <div
                key={c.id}
                className="wb-divider-row"
                style={{ flexDirection: 'column', alignItems: 'stretch', gap: 4 }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <Badge tone={c.author_role === 'admin' ? 'honey' : 'neutral'}>
                    {c.author_role}
                  </Badge>
                  <span className="wb-tiny">{formatRelative(c.created_at)}</span>
                </div>
                <p style={{ whiteSpace: 'pre-wrap' }}>{c.message}</p>
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop: 14 }}>
          <textarea
            className="wb-field"
            rows={2}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Reply to this ticket…"
          />
          <Button
            size="sm"
            style={{ marginTop: 10 }}
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || !message.trim()}
          >
            {mutation.isPending ? 'Posting…' : 'Add comment'}
          </Button>
          {mutation.isError ? (
            <div className="wb-form-error">{getErrorMessage(mutation.error)}</div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

function DiagnosticPanel({ info }: { info: unknown }) {
  const [open, setOpen] = useState(false);
  return (
    <Card>
      <CardContent>
        <button
          type="button"
          className="wb-eyebrow"
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          onClick={() => setOpen((v) => !v)}
        >
          Diagnostics {open ? '▾' : '▸'}
        </button>
        {open ? (
          <pre
            className="wb-mono"
            style={{
              marginTop: 10,
              fontSize: 12,
              overflowX: 'auto',
              whiteSpace: 'pre-wrap',
              color: 'var(--ink-2)',
            }}
          >
            {JSON.stringify(info, null, 2)}
          </pre>
        ) : null}
      </CardContent>
    </Card>
  );
}

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
      <dt className="wb-tiny">{label}</dt>
      <dd className="wb-small" style={{ textAlign: 'right' }}>
        {value || '—'}
      </dd>
    </div>
  );
}
